import { TKV2_ORDER_ATTACHMENT_API } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import {
  useGetTkv2CommentaryAttachmentAPI,
  usePutTkv2CommentaryAttachmentAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.ts';
import { commentaryMutationComplete, setCommentary } from '../../redux/features/commentarySlice.ts';
import { selectCommentary } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { DEFAULT_SYNC_DRIVER_DELAY_MS } from './consts.ts';

export const useCommentarySyncDriver = (orderID: string) => {
  const dispatch = useAppDispatch();
  const { mutate: commentaryMutate } = usePutTkv2CommentaryAttachmentAPI();
  const { data: persistedCommentaryData } = useGetTkv2CommentaryAttachmentAPI({
    pathParams: { orderID },
  });
  const commentary = useAppSelector(selectCommentary);
  const { editRevision = 0, serverRevision = 0 } = commentary?.syncState ?? {};

  useEffect(() => {
    if (!persistedCommentaryData || commentary.syncState) return;

    const parsedData =
      TKV2_ORDER_ATTACHMENT_API.COMMENTARY.GET.RESPONSE.parse(persistedCommentaryData);
    dispatch(setCommentary(parsedData.body));
  }, [persistedCommentaryData]);

  const onSuccess = () => {
    dispatch(
      commentaryMutationComplete({
        status: 'success',
        serverRevision: editRevision,
      }),
    );
  };

  const onError = () => {
    dispatch(
      commentaryMutationComplete({
        status: 'error',
      }),
    );
  };

  const mutate = () =>
    commentaryMutate(
      {
        pathParams: { orderID },
        body: TKV2_ORDER_ATTACHMENT_API.COMMENTARY.PUT.REQUEST.BODY.parse(commentary),
      },
      { onSuccess, onError },
    );

  const mutationTrigger = useEdgeTrigger({
    condition: editRevision > serverRevision,
    delayMs: DEFAULT_SYNC_DRIVER_DELAY_MS,
    action: mutate,
  });

  useEffect(() => {
    mutationTrigger.restartTimer();
  }, [editRevision, mutationTrigger]);
};
